import { useEffect, useRef, useState } from "react";
import { Form } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import dentallyLogo from "../../../assets/dentally-logo.png";
import PasswordResetInjectors from "../ForgotPassword/injectors";
import CreateAccountInjectors from "../CreateAccount/injectors";
import {
  selectEmail,
  selectRequestReset,
  selectVerifyOtp,
} from "../ForgotPassword/selector";
import { saveRequestReset, saveVerifyOtp } from "../ForgotPassword/slice";
import {
  selectCredentials,
  selectResendSignupOtp,
  selectVerifySignupOtp,
} from "../CreateAccount/selector";
import {
  resendSignupOtp,
  saveVerifySignupOtp,
} from "../CreateAccount/slice";
import { stripWhitespace } from "../../../utils/formNormalizers";
import type { OtpFormValues } from "./types";
import {
  BackButton,
  FormCard,
  ImageCard,
  LeftCol,
  LogoImage,
  PageRow,
  PrimaryButton,
  ResendLink,
  ResendRow,
  ResendText,
  RightCol,
  StyledForm,
  StyledOtpInput,
  StyledTitle,
  SubmitFormItem,
  SubtitleText,
} from "./style";

const { Item, useForm } = Form;
const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 120;

function formatCooldown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export default function OtpPage() {
  const [form] = useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const isSignup =
    (location.state as { flow?: string } | null)?.flow === "signup";

  const resetEmail = useSelector(selectEmail);
  const {
    saving: verifying,
    error: verifyError,
    verified,
  } = useSelector(selectVerifyOtp);
  const { saving: resending } = useSelector(selectRequestReset);

  const credentials = useSelector(selectCredentials);
  const signupVerify = useSelector(selectVerifySignupOtp);
  const { saving: signupResending } = useSelector(selectResendSignupOtp);

  const email = isSignup ? credentials?.email ?? null : resetEmail;

  const wasVerifying = useRef(false);
  const [resendCooldown, setResendCooldown] = useState(
    RESEND_COOLDOWN_SECONDS,
  );

  useEffect(() => {
    if (isSignup) {
      if (!credentials) {
        navigate("/signup", { replace: true });
      }
    } else if (!resetEmail) {
      navigate("/forgot-password", { replace: true });
    }
  }, [isSignup, credentials, resetEmail, navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timerId = setTimeout(() => {
      setResendCooldown((seconds) => (seconds <= 1 ? 0 : seconds - 1));
    }, 1000);
    return () => clearTimeout(timerId);
  }, [resendCooldown]);

  useEffect(() => {
    if (isSignup) return;
    if (wasVerifying.current && !verifying && !verifyError && verified) {
      navigate("/reset-password");
    }
    wasVerifying.current = verifying;
  }, [isSignup, verifying, verifyError, verified, navigate]);

  function handleFinish(values: unknown) {
    const v = values as OtpFormValues;
    if (isSignup) {
      if (!credentials) return;
      dispatch(saveVerifySignupOtp({ otp: v.code }));
      return;
    }
    if (!resetEmail) return;
    dispatch(saveVerifyOtp({ email: resetEmail, otp: v.code }));
  }

  function handleResend() {
    if (resendCooldown > 0) return;
    if (isSignup) {
      if (!credentials || signupResending) return;
      dispatch(resendSignupOtp());
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      return;
    }
    if (!resetEmail || resending) return;
    dispatch(saveRequestReset({ email: resetEmail }));
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
  }

  const submitting = isSignup ? signupVerify.saving : verifying;
  const resendInFlight = isSignup ? signupResending : resending;

  return (
    <PageRow>
      <PasswordResetInjectors />
      <CreateAccountInjectors />
      <LeftCol xs={0} sm={0} md={0} lg={12} xl={12}>
        <ImageCard />
      </LeftCol>

      <RightCol xs={24} sm={24} md={24} lg={12} xl={12}>
        <FormCard vertical align="flex-start">
          <LogoImage
            src={dentallyLogo}
            alt="Dentally"
            width={233}
            preview={false}
          />

          <BackButton
            aria-label="Back"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(isSignup ? "/login" : "/forgot-password")}
          />

          <StyledTitle level={1}>Verification Code</StyledTitle>

          <SubtitleText>
            We sent you a verification code on {email ?? ""}
          </SubtitleText>

          <StyledForm
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            requiredMark={false}
          >
            <Item
              name="code"
              normalize={stripWhitespace}
              rules={[
                { required: true, message: "Please enter the code" },
                {
                  len: OTP_LENGTH,
                  message: `Code must be ${OTP_LENGTH} digits`,
                },
              ]}
            >
              <StyledOtpInput length={OTP_LENGTH} />
            </Item>

            <SubmitFormItem>
              <PrimaryButton
                type="primary"
                htmlType="submit"
                block
                loading={submitting}
              >
                Verify Code
              </PrimaryButton>
            </SubmitFormItem>
          </StyledForm>

          <ResendRow>
            <ResendText>Didn't receive the code?</ResendText>
            <ResendLink
              onClick={handleResend}
              disabled={resendInFlight || resendCooldown > 0}
            >
              {resendInFlight
                ? "Sending…"
                : resendCooldown > 0
                  ? `Resend in ${formatCooldown(resendCooldown)}`
                  : "Resend"}
            </ResendLink>
          </ResendRow>
        </FormCard>
      </RightCol>
    </PageRow>
  );
}
